import { apiClient } from './client'
import type {
  LessonScheduleTemplate,
} from '@/types'

type LessonScheduleSlotInput = {
  weekday: number
  start_time: string // HH:mm:ss
  end_time: string // HH:mm:ss
}

type LessonSchedulePauseInput = {
  id?: number
  date_from: string // YYYY-MM-DD
  date_to: string // YYYY-MM-DD
  reason?: string | null
}

type LessonScheduleUpsertPayload = {
  is_active?: boolean
}

type ApiWrapper<T> = {
  message?: string
  data?: T | null
}

function unwrapTemplate<T>(responseData: ApiWrapper<T> | T): T | null {
  if (responseData && typeof responseData === 'object' && 'data' in responseData) {
    return (responseData as ApiWrapper<T>).data ?? null
  }
  return (responseData as T) ?? null
}

export const lessonSchedulesApi = {
  // User schedules
  getUserSchedule: async (userId: number): Promise<LessonScheduleTemplate | null> => {
    const response = await apiClient.get<ApiWrapper<LessonScheduleTemplate> | { data: LessonScheduleTemplate | null }>(
      `/users/${userId}/lesson-schedules`
    )
    return unwrapTemplate<LessonScheduleTemplate>(response.data)
  },

  upsertUserSchedule: async (userId: number, payload: LessonScheduleUpsertPayload) => {
    const response = await apiClient.post<ApiWrapper<LessonScheduleTemplate>>(
      `/users/${userId}/lesson-schedules`,
      payload
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка сохранения расписания ученика')
  },

  updateUserSlots: async (
    userId: number,
    scheduleId: number,
    slots: LessonScheduleSlotInput[]
  ): Promise<LessonScheduleTemplate> => {
    const response = await apiClient.put<ApiWrapper<LessonScheduleTemplate>>(
      `/users/${userId}/lesson-schedules/${scheduleId}/slots`,
      { slots }
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления слотов ученика')
  },

  updateUserPauses: async (
    userId: number,
    scheduleId: number,
    pauses: LessonSchedulePauseInput[]
  ): Promise<LessonScheduleTemplate> => {
    const response = await apiClient.put<ApiWrapper<LessonScheduleTemplate>>(
      `/users/${userId}/lesson-schedules/${scheduleId}/pauses`,
      { pauses }
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления пауз ученика')
  },

  // Group schedules
  getGroupSchedule: async (groupId: number): Promise<LessonScheduleTemplate | null> => {
    const response = await apiClient.get<ApiWrapper<LessonScheduleTemplate> | { data: LessonScheduleTemplate | null }>(
      `/groups/${groupId}/lesson-schedules`
    )
    return unwrapTemplate<LessonScheduleTemplate>(response.data)
  },

  upsertGroupSchedule: async (groupId: number, payload: LessonScheduleUpsertPayload) => {
    const response = await apiClient.post<ApiWrapper<LessonScheduleTemplate>>(
      `/groups/${groupId}/lesson-schedules`,
      payload
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка сохранения расписания группы')
  },

  updateGroupSlots: async (
    groupId: number,
    scheduleId: number,
    slots: LessonScheduleSlotInput[]
  ): Promise<LessonScheduleTemplate> => {
    const response = await apiClient.put<ApiWrapper<LessonScheduleTemplate>>(
      `/groups/${groupId}/lesson-schedules/${scheduleId}/slots`,
      { slots }
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления слотов группы')
  },

  updateGroupPauses: async (
    groupId: number,
    scheduleId: number,
    pauses: LessonSchedulePauseInput[]
  ): Promise<LessonScheduleTemplate> => {
    const response = await apiClient.put<ApiWrapper<LessonScheduleTemplate>>(
      `/groups/${groupId}/lesson-schedules/${scheduleId}/pauses`,
      { pauses }
    )

    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка обновления пауз группы')
  },
}

